import { formBuilderFromDocument } from "./generate-form-layout";
import { DocumentFormSource } from "./DocumentFormSource";
import { LegacyFeatureSummary } from "./LegacyFeatureSummary";
import { ProfileConflicts } from "./ProfileConflicts";
import { generateFormLayout, restoreFormLayout } from "./generate-form-layout";
import { HostFormData } from "./HostFormData";
import { useState, useMemo } from "react";
import {
  Plus,
  Search,
  Trash2,
  SlidersHorizontal,
  ChevronDown,
  Layers3,
  Link2,
  UserRoundPlus,
  Copy,
  ArrowDown,
  ArrowUp,
  Sparkles,
  FlaskConical,
} from "lucide-react";
import {
  applyFieldResourceDefaults,
  effectiveFields,
  isDynamicChoiceField,
  newField,
  uid,
  fieldTypes,
  imageTools,
  setPartOverride,
  clearPartOverrideProperty,
  deleteField,
  parseProject,
  exposeFormInput,
  suggestFormMapping,
  formFieldCompatible,
  type Project,
  type Field,
  type Condition,
  fieldSchema,
  mapSettingsSchema,
  addressParts,
  coordinateParts,
  qrParts,
  type Part,
  qrTypes,
  reusableFormSchema,
  suggestFormFromDocument,
  validationCapabilities,
  applicableValidation,
  assignGlobalProfileCollection,
  applyResourceSnapshot,
} from "@proof/core";
import { FieldPolicyControls } from "./FieldPolicies";
import { DatasetManager, DatasetSource } from "./Datasets";
import { ImageChoices } from "./ImageChoices";
import { PresetRecords } from "./PresetRecords";
import { CalculationEditor } from "./CalculationEditor";
import { useAuthoringServices, useEditorText } from "./services";
import { projectEditorFields } from "./project-editor-fields";
import { FormGenerator } from "@proof/ui";
import {
  Select,
  Button,
  Control,
  CurrencyInput,
  Toggle,
  JsonEditor,
  Modal,
  Empty,
  SetupModes,
  type SetupMode,
} from "./components";
import { FormLayoutBuilder } from "./FormLayoutBuilder";
import { AddFieldDialog } from "./AddFieldDialog";
import { RuleBuilder } from "./RuleBuilder";
import { fieldTypeInfo, fieldTypeName } from "./field-types";
export interface EditorProps {
  project: Project;
  onChange: (project: Project) => void;
  notify: (message: string) => void;
}
export function Configuration({
  project: p,
  onChange,
  notify,
  fieldsOnly = false,
  management = false,
  initialView = "fields",
}: EditorProps & {
  /** Only the field list and full field settings (used for profile types). */
  fieldsOnly?: boolean;
  management?: boolean;
  initialView?: string;
}) {
  const services = useAuthoringServices();
  const text = useEditorText();
  const feature = (
    key: keyof NonNullable<typeof services.editorPresentation>,
  ) => services.editorPresentation?.[key] !== false;
  const [generatedBefore, setGeneratedBefore] = useState<Project>();
  const [partId, setPartId] = useState(""),
    [selectedId, setSelected] = useState(p.fields[0]?.id || ""),
    [search, setSearch] = useState(""),
    [view, setView] = useState(initialView),
    [showForm, setShowForm] = useState(false),
    [globalForm, setGlobalForm] = useState<"form" | "profile">(),
    [adding, setAdding] = useState(false),
    [rulesMode, setRulesMode] = useState<SetupMode>("guided"),
    // Header filters: off applies no filter; on shows only matching fields.
    [fieldFilters, setFieldFilters] = useState(() => ({
      system: services.editorPresentation?.fieldFilters?.system === true,
      calculated:
        services.editorPresentation?.fieldFilters?.calculated === true,
      required: services.editorPresentation?.fieldFilters?.required === true,
    })),
    [previewValues, setPreviewValues] = useState<Values>({});
  const damServices = useMemo(
    () => services.dam?.(p, partId || undefined),
    [services.dam, p, partId],
  );
  const part = p.parts.find((x) => x.id === partId),
    fields = effectiveFields(p, partId || undefined),
    // A stale or empty selection (e.g. a newly loaded version) shows the first field.
    field = fields.find((f) => f.id === selectedId) ?? fields[0];
  const change = (next: Project) => {
    try {
      onChange(parseProject(next));
    } catch (e) {
      notify((e as Error).message);
    }
  };
  const update = (patch: Partial<Field>) => {
    if (!field) return;
    if (part) change(setPartOverride(p, part.id, field.id, patch));
    else
      change({
        ...p,
        fields: p.fields.map((f) =>
          f.id === field.id ? { ...f, ...patch } : f,
        ),
      });
  };
  const reset = (key: keyof Field) => {
    if (!part || !field) return;
    change(clearPartOverrideProperty(p, part.id, field.id, key));
  };
  const inherited = (key: keyof Field) =>
    part &&
    field &&
    part.overrides[field.id] &&
    Object.hasOwn(part.overrides[field.id], key)
      ? () => reset(key)
      : undefined;
  const add = (f: Field) => {
    change({ ...p, fields: [...p.fields, f] });
    setSelected(f.id);
    setPartId("");
    setAdding(false);
  };
  return (
    <div className={`configuration ${fieldsOnly ? "fields-only" : ""}`}>
      <div className="page-intro" hidden={fieldsOnly}>
        <div>
          {services.editorPresentation?.showHeading && (
            <h1>{text("fields.heading")}</h1>
          )}
          {services.editorPresentation?.showDescription && (
            <p>{text("fields.description")}</p>
          )}
        </div>
        <div className="button-row page-intro-actions">
          {services.saveResource && feature("enableGlobalForms") && (
            <>
              <Button
                title="Generate a shared form, then choose whether to copy it or link a project"
                onClick={() => setGlobalForm("form")}
              >
                <Link2 size={15} />
                {text("fields.action.generateGlobalForm")}
              </Button>
              <Button
                title="Create a global profile type from this document's fields and map it to this project"
                onClick={() => setGlobalForm("profile")}
              >
                <UserRoundPlus size={15} />
                {text("fields.action.generateProfileForm")}
              </Button>
            </>
          )}
          {feature("enableFormGeneration") && (
            <Button
              disabled={!p.fields.length}
              onClick={() => {
                setGeneratedBefore(p);
                change(generateFormLayout(p));
              }}
            >
              <Sparkles size={15} />
              {text("fields.action.generateForm")}
            </Button>
          )}
          {generatedBefore && <p role="status">Layout generated</p>}
          {generatedBefore && (
            <Button
              onClick={() => {
                change(restoreFormLayout(p, generatedBefore));
                setGeneratedBefore(undefined);
              }}
            >
              Undo generated layout
            </Button>
          )}
          {feature("enableFormPreview") && (
            <Button onClick={() => setShowForm(true)}>
              <Layers3 size={16} />
              {text("fields.action.viewForm")}
            </Button>
          )}
          {services.testForm && !management && !fieldsOnly && (
            <Button onClick={() => services.testForm?.(p)}>
              <FlaskConical size={16} />
              {text("fields.action.testForm")}
            </Button>
          )}
        </div>
      </div>
      {globalForm && (
        <GlobalFormDialog
          project={p}
          kind={globalForm}
          onClose={() => setGlobalForm(undefined)}
          onDone={(next, message) => {
            change(next);
            setGlobalForm(undefined);
            notify(message);
          }}
        />
      )}
      <LegacyFeatureSummary project={p} />
      <ProfileConflicts project={p} />
      <div className="section-tabs" hidden={fieldsOnly}>
        {[
          ["fields", text("fields.tab.fields")],
          ["layout", text("fields.tab.layout")],
          ["parts", text("fields.tab.parts")],
          ["forms", text("fields.tab.forms")],
          [
            "rules",
            feature("enableRules")
              ? text("fields.tab.rules")
              : text("fields.tab.data"),
          ],
        ]
          .filter(
            ([id]) =>
              id !== "forms" &&
              (id !== "parts" || feature("enablePartOverrides")) &&
              (id !== "layout" || feature("enableFormLayout")) &&
              (id !== "rules" || feature("enableDataPresets")),
          )
          .map(([id, label]) => (
            <button
              key={id}
              className={view === id ? "active" : ""}
              onClick={() => setView(id)}
            >
              {label}
            </button>
          ))}
      </div>
      {view === "fields" && (
        <div className="configuration-workspace">
          <section className="field-list panel">
            <header>
              <div>
                <h3>
                  {text("fields.list.heading")}{" "}
                  <span className="count">{p.fields.length}</span>
                </h3>
                <p className="muted">{text("fields.list.description")}</p>
              </div>
              <div className="add-field">
                <Button kind="primary" onClick={() => setAdding(true)}>
                  <Plus size={14} />
                  {text("fields.action.addField")}
                </Button>
              </div>
            </header>
            {services.editorPresentation?.enableFieldFilters !== false && (
              <div
                className="field-list-filters"
                role="group"
                aria-label="Show only"
              >
                {(
                  [
                    ["system", "System"],
                    ["calculated", "Calculated"],
                    ["required", "Required"],
                  ] as const
                ).map(([key, label]) => (
                  <Toggle
                    key={key}
                    label={label}
                    value={fieldFilters[key]}
                    onChange={(value) =>
                      setFieldFilters((current) => ({
                        ...current,
                        [key]: value,
                      }))
                    }
                  />
                ))}
              </div>
            )}
            <div className="search-input">
              <Search size={15} />
              <input
                aria-label="Search fields"
                placeholder="Find a field…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="field-list-items">
              {fields
                .map((f, i) => [f, i] as const)
                .filter(
                  ([f]) =>
                    (!fieldFilters.system || f.system) &&
                    (!fieldFilters.calculated || !!f.calculation) &&
                    (!fieldFilters.required || f.required) &&
                    `${f.label} ${f.key}`
                      .toLowerCase()
                      .includes(search.toLowerCase()),
                )
                .map(([f, i]) => (
                  <button
                    key={f.id}
                    className={`field-row ${field?.id === f.id ? "selected" : ""}`}
                    onClick={() => setSelected(f.id)}
                  >
                    <span
                      className="field-type-icon"
                      title={fieldTypeName(f.type)}
                    >
                      {fieldTypeInfo[f.type]?.icon || "T"}
                    </span>
                    <span>
                      <strong>{f.label}</strong>
                      <small>{f.key || "Helper selector"}</small>
                    </span>
                    <span className="field-row-meta">
                      {f.calculation && (
                        <span className="badge blue">Calculated rule</span>
                      )}
                      {part?.overrides[f.id] ? (
                        <span className="badge blue">Override</span>
                      ) : f.required ? (
                        <span className="tiny-dot" title="Required" />
                      ) : null}
                      <small>{String(i + 1).padStart(2, "0")}</small>
                    </span>
                  </button>
                ))}
            </div>
            {!fields.length && (
              <Empty
                title="Start with a field"
                description="Add the information your documents need."
              />
            )}
            <footer>
              <span>
                <SlidersHorizontal size={13} />{" "}
                {fields.filter((f) => f.required).length} required ·{" "}
                {fields.filter((f) => f.system).length} system
              </span>
            </footer>
          </section>
          <section className="field-inspector panel">
            <header>
              <div>
                <span className="eyebrow">FIELD SETTINGS</span>
                <h3>{field?.label || "Select a field"}</h3>
              </div>
              {field && !part && (
                <button
                  className="icon-button danger"
                  aria-label="Delete field"
                  onClick={() => {
                    try {
                      change(deleteField(p, field.id));
                      setSelected(
                        p.fields.find((f) => f.id !== field.id)?.id || "",
                      );
                    } catch (error) {
                      notify((error as Error).message);
                    }
                  }}
                >
                  <Trash2 size={16} />
                </button>
              )}
            </header>
            <div className="scope-banner" hidden={fieldsOnly}>
              <span>Editing scope</span>
              <Select
                aria-label="Editing scope"
                value={partId}
                onChange={(e) => {
                  setPreviewValues({});
                  setPartId(e.target.value);
                }}
              >
                <option value="">Main project</option>
                {p.parts.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
              </Select>
            </div>
            {part && (
              <div className="inheritance-note">
                <strong>
                  {field && part.overrides[field.id]
                    ? "This variable has an override."
                    : "Inherited from the main project."}
                </strong>
                <p>Only settings you change are saved for this part.</p>
                {field && part.overrides[field.id] && (
                  <button
                    className="text-button"
                    onClick={() =>
                      change(setPartOverride(p, part.id, field.id, null))
                    }
                  >
                    Remove all overrides for this variable
                  </button>
                )}
              </div>
            )}
            {field && (
              <div className="inspector-body">
                <div className="control-grid">
                  <Control label="Display label" reset={inherited("label")}>
                    <input
                      value={field.label}
                      onChange={(e) => update({ label: e.target.value })}
                    />
                  </Control>
                  <Control
                    label="Variable token"
                    help="The name designers use to place this value in the document. Changing it does not move existing bindings."
                    helpDocs="/help?guide=variables"
                  >
                    <input
                      value={field.key}
                      disabled={!!part}
                      onChange={(e) => update({ key: e.target.value })}
                    />
                  </Control>
                  <Control
                    label="Control type"
                    reset={inherited("type")}
                    help="How people enter this value on the form. Choose the control that matches the information."
                    helpDocs="/help?guide=variables"
                    helpDetails={<ControlTypeHelp />}
                  >
                    <Select
                      value={field.type}
                      onChange={(e) => {
                        const image =
                          ["image", "image-list"].includes(e.target.value) &&
                          !["image", "image-list"].includes(field.type);
                        if (
                          image &&
                          (field.default ||
                            field.choices.length ||
                            field.source.kind !== "literal") &&
                          !window.confirm(
                            "Switch to image data? The existing field data will be replaced with image uploads. Continue?",
                          )
                        )
                          return;
                        update({
                          ...(image
                            ? {
                                default: "",
                                choices: [],
                                source: { kind: "literal" } as const,
                              }
                            : {}),
                          type: e.target.value as Field["type"],
                          // Keep only validation that applies to the new type.
                          validation: applicableValidation(
                            e.target.value,
                            field.validation,
                          ),
                          ...(field.source.kind === "dam" &&
                          !["image", "image-list"].includes(e.target.value)
                            ? { source: { kind: "literal" } }
                            : {}),
                          ...(field.source.kind === "statement" &&
                          !["text", "textarea", "richtext"].includes(
                            e.target.value,
                          )
                            ? { source: { kind: "literal" } }
                            : {}),
                        });
                      }}
                    >
                      {fieldTypes.map((t) => (
                        <option key={t} value={t}>
                          {fieldTypeName(t)}
                        </option>
                      ))}
                    </Select>
                  </Control>
                  <Control
                    label="Form group"
                    reset={inherited("groupId")}
                    help="The form section this field appears in. Arrange sections and columns in Form layout."
                    helpDocs="/help?guide=forms"
                  >
                    <Select
                      value={field.groupId || ""}
                      onChange={(e) =>
                        update({ groupId: e.target.value || undefined })
                      }
                    >
                      <option value="">Ungrouped</option>
                      {p.groups.map((g) => (
                        <option key={g.id} value={g.id}>
                          {g.name}
                        </option>
                      ))}
                    </Select>
                  </Control>
                </div>
                <Control
                  label="Helper text"
                  reset={inherited("help")}
                  help="Short guidance shown under the control on the form."
                >
                  <textarea
                    rows={2}
                    value={field.help}
                    onChange={(e) => update({ help: e.target.value })}
                  />
                </Control>
                <div className="toggle-grid">
                  <Toggle
                    label="Required"
                    value={field.required}
                    onChange={(v) => update({ required: v })}
                  />
                  <Toggle
                    label="Editable"
                    disabled={!!field.calculation}
                    value={field.editable}
                    onChange={(v) => update({ editable: v })}
                  />
                  <Toggle
                    label="Hidden"
                    value={field.hidden}
                    onChange={(v) => update({ hidden: v })}
                  />
                  <Toggle
                    label="System variable"
                    value={field.system}
                    onChange={(v) => update({ system: v })}
                  />
                  <Toggle
                    label="Use in proof name"
                    value={field.proofName}
                    onChange={(v) => update({ proofName: v })}
                  />
                  <Toggle
                    label="Auto-generated"
                    disabled={!!field.calculation}
                    value={field.autoGenerated}
                    onChange={(v) => update({ autoGenerated: v })}
                  />
                </div>
                {feature("enableFormDisplay") && (
                  <details open>
                    <summary>{text("fields.section.display")}</summary>
                    <Control
                      label="Form display"
                      reset={inherited("formDisplay")}
                      help="Show, hide or remove this control on the main form. Values and document bindings are kept either way."
                      helpDocs="/help?guide=forms"
                      note="Hide retains the control in the form editor. Remove puts it in Available fields. Both retain the variable, value and document bindings."
                    >
                      <Select
                        value={field.formDisplay || "show"}
                        onChange={(e) =>
                          update({
                            formDisplay: e.target.value as Field["formDisplay"],
                          })
                        }
                      >
                        <option value="show">Show on main form</option>
                        <option value="hide">Hide on main form</option>
                        <option value="remove">Remove from main form</option>
                      </Select>
                    </Control>
                  </details>
                )}
                <details
                  hidden={
                    services.editorPresentation?.enableCalculatedValues ===
                      false || !feature("enableRules")
                  }
                  open={!!field.calculation}
                >
                  <summary>
                    {field.calculation
                      ? "Calculated rule"
                      : text("fields.section.calculated")}
                  </summary>
                  <Control
                    label="Calculation inheritance"
                    reset={inherited("calculation")}
                  >
                    <Select
                      value={field.calculation ? "calculated" : "manual"}
                      onChange={(e) =>
                        update(
                          e.target.value === "manual"
                            ? { calculation: null }
                            : {
                                calculation: { op: "literal", value: "" },
                                ...(field.source.kind !== "literal"
                                  ? { source: { kind: "literal" as const } }
                                  : {}),
                                ...(field.autoGenerated
                                  ? { autoGenerated: false }
                                  : {}),
                              },
                        )
                      }
                    >
                      <option value="manual">
                        Use the configured default and input
                      </option>
                      <option value="calculated">
                        Calculate from variables and rules
                      </option>
                    </Select>
                  </Control>
                  {field.calculation && (
                    <>
                      <p className="muted">
                        Calculated values update with the form and cannot be
                        entered or changed by a saved preset. Text replacements
                        are literal, not scripts.
                      </p>
                      <CalculationEditor
                        value={field.calculation}
                        fields={p.fields.filter((f) => f.id !== field.id)}
                        onChange={(calculation) => update({ calculation })}
                      />
                    </>
                  )}
                </details>
                <details open>
                  <summary>Default & data source</summary>
                  {field.type === "image-list" &&
                    field.source.kind !== "dam" && (
                      <Control
                        label="Image choice display"
                        reset={inherited("choicePresentation")}
                      >
                        <Select
                          value={field.choicePresentation || "select"}
                          onChange={(event) =>
                            update({
                              choicePresentation: event.target
                                .value as Field["choicePresentation"],
                            })
                          }
                        >
                          <option value="select">Dropdown</option>
                          <option value="thumbnails">Thumbnail gallery</option>
                        </Select>
                      </Control>
                    )}
                  {!field.calculation && (
                    <DatasetSource
                      project={p}
                      field={field}
                      update={update}
                      reset={inherited("source")}
                      notify={notify}
                    />
                  )}
                  {field.calculation && (
                    <p className="muted">
                      The calculated value supplies this field. Disable its
                      calculation to configure a default or another data source.
                    </p>
                  )}
                  <Control
                    label="Default value"
                    reset={inherited("default")}
                    help="The value used until someone changes it. Parts can override it."
                    helpDocs="/help?guide=defaults"
                  >
                    {field.type === "checkbox" &&
                    isDynamicChoiceField(field) ? (
                      <input
                        aria-label="Default choice selections"
                        value={
                          Array.isArray(field.default)
                            ? field.default.join(";")
                            : ""
                        }
                        placeholder="Semicolon-separated values"
                        onChange={(event) =>
                          update({
                            default: event.target.value
                              .split(";")
                              .filter(Boolean),
                          })
                        }
                      />
                    ) : field.type === "checkbox" && !field.choices.length ? (
                      <Toggle
                        label="Checked by default"
                        value={field.default === true}
                        onChange={(v) => update({ default: v })}
                      />
                    ) : (
                      <input
                        value={
                          typeof field.default === "object"
                            ? JSON.stringify(field.default)
                            : String(field.default ?? "")
                        }
                        onChange={(e) => update({ default: e.target.value })}
                      />
                    )}
                  </Control>
                  <Control
                    label="Price upcharge"
                    reset={inherited("price")}
                    help="Added to the order price when this field has a value."
                  >
                    <CurrencyInput
                      aria-label="Price upcharge"
                      value={field.price}
                      onChange={(price) => update({ price })}
                    />
                  </Control>
                </details>
                {validationCapabilities(field.type).supported &&
                  feature("enableValidation") && (
                    <details>
                      <summary>{text("fields.section.validation")}</summary>
                      <FieldPolicyControls
                        project={p}
                        field={field}
                        onChange={update}
                        sharedOnly={fieldsOnly}
                        kinds={["validation"]}
                      />
                    </details>
                  )}
                {![
                  "image",
                  "image-list",
                  "qr",
                  "map",
                  "checkbox",
                  "preset",
                  "address",
                ].includes(field.type) &&
                  feature("enableFormatting") && (
                    <details>
                      <summary>{text("fields.section.formatting")}</summary>
                      <FieldPolicyControls
                        project={p}
                        field={field}
                        onChange={update}
                        sharedOnly={fieldsOnly}
                        kinds={["format"]}
                      />
                    </details>
                  )}
                {[
                  "dropdown",
                  "combobox",
                  "radio",
                  "checkbox",
                  "image-list",
                ].includes(field.type) && (
                  <details open>
                    <summary>Choices</summary>
                    {!field.choices.length &&
                      !["dataset", "dynamic"].includes(field.source.kind) && (
                        <p role="status">
                          {field.type === "checkbox"
                            ? "This existing field uses a single yes/no checkbox. Enter options below to use a multiple-choice checkbox group, or choose a dataset in Data source."
                            : "Enter options below or bind a dataset in Data source."}
                        </p>
                      )}
                    <Control
                      label="Options"
                      note="One option per line. Use value | label for separate labels."
                    >
                      <textarea
                        rows={5}
                        disabled={field.source.kind === "dataset"}
                        value={field.choices
                          .map((c) =>
                            c.value === c.label
                              ? c.value
                              : `${c.value} | ${c.label}`,
                          )
                          .join("\n")}
                        onChange={(e) =>
                          update({
                            choices: e.target.value
                              .split("\n")
                              .filter(Boolean)
                              .map((v) => {
                                const [value, label] = v
                                  .split("|")
                                  .map((v) => v.trim());
                                return {
                                  ...field.choices.find(
                                    (c) => c.value === value,
                                  ),
                                  value,
                                  label: label || value,
                                };
                              }),
                          })
                        }
                      />
                    </Control>
                    {field.type === "image-list" && (
                      <ImageChoices
                        key={JSON.stringify([
                          p.tenantId,
                          p.id,
                          partId,
                          field.id,
                        ])}
                        field={field}
                        onChange={update}
                      />
                    )}
                  </details>
                )}
                <details hidden={!feature("enableConditionalVisibility")}>
                  <summary>Conditional visibility</summary>
                  <Control label="Source field">
                    <Select
                      value={field.visibleWhen?.field || ""}
                      onChange={(e) =>
                        update({
                          visibleWhen: e.target.value
                            ? { field: e.target.value, op: "eq", value: "" }
                            : undefined,
                        })
                      }
                    >
                      <option value="">Always visible</option>
                      {p.fields
                        .filter((f) => f.id !== field.id)
                        .map((f) => (
                          <option key={f.id} value={f.id}>
                            {f.label}
                          </option>
                        ))}
                    </Select>
                  </Control>
                  {field.visibleWhen?.field && (
                    <div className="control-grid">
                      <Control label="Operator">
                        <Select
                          value={field.visibleWhen.op}
                          onChange={(e) =>
                            update({
                              visibleWhen: {
                                ...field.visibleWhen,
                                op: e.target.value as Condition["op"],
                              },
                            })
                          }
                        >
                          {[
                            ["eq", "is"],
                            ["neq", "is not"],
                            ["empty", "is empty"],
                            ["notEmpty", "is filled in"],
                            ["startsWith", "Starts with"],
                            ["endsWith", "Ends with"],
                            ["contains", "contains"],
                            ["notContains", "does not contain"],
                            ["matches", "matches pattern"],
                            ["gt", "is greater than"],
                            ["lt", "is less than"],
                          ].map(([op, label]) => (
                            <option key={op} value={op}>
                              {label}
                            </option>
                          ))}
                        </Select>
                      </Control>
                      <Control label="Value">
                        <input
                          value={String(field.visibleWhen.value ?? "")}
                          onChange={(e) =>
                            update({
                              visibleWhen: {
                                ...field.visibleWhen,
                                value: e.target.value,
                              },
                            })
                          }
                        />
                      </Control>
                    </div>
                  )}
                  <Control label="Rule action">
                    <Select
                      value={field.visibilityAction}
                      onChange={(e) =>
                        update({
                          visibilityAction: e.target
                            .value as Field["visibilityAction"],
                        })
                      }
                    >
                      <option value="show">Show when matched</option>
                      <option value="hide">Hide when matched</option>
                    </Select>
                  </Control>
                </details>
                {["image", "map", "image-list"].includes(field.type) && (
                  <details open>
                    <summary>Image settings</summary>
                    <Control label="Upload limit (MB)">
                      <input
                        type="number"
                        min="1"
                        max="20"
                        value={field.image.maxBytes / 1000000}
                        onChange={(e) =>
                          update({
                            image: {
                              ...field.image,
                              maxBytes: Number(e.target.value) * 1000000,
                            },
                          })
                        }
                      />
                    </Control>
                    <Control label="Minimum image DPI">
                      <input
                        type="number"
                        min="0"
                        value={field.image.minDpi}
                        onChange={(e) =>
                          update({
                            image: {
                              ...field.image,
                              minDpi: Number(e.target.value),
                            },
                          })
                        }
                      />
                    </Control>
                    <Toggle
                      label="Allow upload"
                      value={field.image.allowUpload}
                      onChange={(v) =>
                        update({ image: { ...field.image, allowUpload: v } })
                      }
                    />
                    <Toggle
                      label="Fixed crop aspect"
                      value={field.image.fixedAspect}
                      onChange={(v) =>
                        update({ image: { ...field.image, fixedAspect: v } })
                      }
                    />
                    {field.image.fixedAspect && (
                      <Control label="Crop aspect ratio (width / height)">
                        <input
                          type="number"
                          min="0.01"
                          step="0.01"
                          value={field.image.aspect}
                          onChange={(event) => {
                            const aspect = Number(event.target.value);
                            if (Number.isFinite(aspect) && aspect > 0)
                              update({ image: { ...field.image, aspect } });
                          }}
                        />
                      </Control>
                    )}
                    <div className="image-tool-picker">
                      {imageTools.map((tool) => (
                        <label key={tool}>
                          <input
                            type="checkbox"
                            checked={field.image.tools.includes(tool)}
                            onChange={(e) =>
                              update({
                                image: {
                                  ...field.image,
                                  tools: e.target.checked
                                    ? [...field.image.tools, tool]
                                    : field.image.tools.filter(
                                        (t) => t !== tool,
                                      ),
                                },
                              })
                            }
                          />
                          {tool}
                        </label>
                      ))}
                    </div>
                  </details>
                )}
                {field.type === "map" && (
                  <details open>
                    <summary>Map generation</summary>
                    <MapSources
                      field={field}
                      fields={p.fields}
                      onChange={(map) => update({ map })}
                    />
                    {(["width", "height", "zoom"] as const).map((key) => (
                      <Control key={key} label={`Map ${key}`}>
                        <input
                          type="number"
                          min={key === "zoom" ? 1 : 128}
                          max={key === "zoom" ? 20 : 1280}
                          value={mapSettingsSchema.parse(field.map || {})[key]}
                          onChange={(e) =>
                            update({
                              map: {
                                ...mapSettingsSchema.parse(field.map || {}),
                                [key]: Number(e.target.value),
                              },
                            })
                          }
                        />
                      </Control>
                    ))}
                    <Control label="Map style">
                      <Select
                        value={field.map?.style || "roadmap"}
                        onChange={(e) =>
                          update({
                            map: {
                              ...mapSettingsSchema.parse(field.map || {}),
                              style: e.target.value as NonNullable<
                                Field["map"]
                              >["style"],
                            },
                          })
                        }
                      >
                        {["roadmap", "satellite", "terrain", "hybrid"].map(
                          (style) => (
                            <option key={style}>{style}</option>
                          ),
                        )}
                      </Select>
                    </Control>
                  </details>
                )}
                {field.type === "qr" && (
                  <details open>
                    <summary>QR code</summary>
                    <QrSettings
                      field={field}
                      fields={p.fields}
                      onChange={(qr) => update({ qr })}
                    />
                  </details>
                )}
                {field.type === "preset" && (
                  <details open>
                    <summary>Preset records</summary>
                    <PresetRecords
                      project={p}
                      partId={partId || undefined}
                      key={JSON.stringify([p.tenantId, p.id, partId, field.id])}
                      field={field}
                      fields={fields}
                      onChange={update}
                    />
                    <details>
                      <summary>Advanced preset data</summary>
                      <JsonEditor
                        value={field.presets}
                        onSave={(v) =>
                          update({ presets: v as Field["presets"] })
                        }
                      />
                    </details>
                  </details>
                )}
                {feature("enableConditionalVisibility") &&
                  feature("enableJsonConfiguration") && (
                    <details>
                      <summary>Nested ALL / ANY expression</summary>
                      <JsonEditor
                        label="Nested visibility rule"
                        field={projectEditorFields(p).condition}
                        value={field.visibleWhen || { all: [] }}
                        onSave={(v) => update({ visibleWhen: v as Condition })}
                      />
                    </details>
                  )}
                <div className="inspector-footer">
                  <Button
                    onClick={() => {
                      if (!field) return;
                      const clone = fieldSchema.parse({
                        ...field,
                        id: uid("field"),
                        key: `${field.key}Copy`,
                        label: `${field.label} copy`,
                        order: p.fields.length,
                      });
                      change({ ...p, fields: [...p.fields, clone] });
                      setSelected(clone.id);
                      setPartId("");
                    }}
                  >
                    <Copy size={14} />
                    Duplicate field
                  </Button>
                  {!part && (
                    <>
                      <button
                        className="icon-button"
                        aria-label="Move field up"
                        onClick={() => update({ order: field.order - 1 })}
                      >
                        <ArrowUp size={16} />
                      </button>
                      <button
                        className="icon-button"
                        aria-label="Move field down"
                        onClick={() => update({ order: field.order + 1 })}
                      >
                        <ArrowDown size={16} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </section>
        </div>
      )}
      {view === "layout" && (
        <FormLayout project={p} onChange={change} notify={notify} />
      )}
      {view === "parts" && (
        <PartEditor
          project={p}
          onChange={change}
          notify={notify}
          onEdit={(id) => {
            setPartId(id);
            setView("fields");
          }}
        />
      )}
      {management && view === "forms" && (
        // Retained only for hosts that still open this view directly.
        <ReusableForms project={p} onChange={change} notify={notify} />
      )}
      {view === "rules" && (
        <section className="panel padded">
          <div className="rules-heading" hidden={!feature("enableRules")}>
            <div>
              <h3>Conditional trigger rules</h3>
              <p className="muted">
                Show or hide fields and sections, clear values, or exclude
                choices when another answer matches.
              </p>
            </div>
            <SetupModes
              label="Rules setup"
              guidedLabel="Rule builder"
              value={rulesMode}
              onChange={setRulesMode}
            />
          </div>
          {feature("enableRules") &&
            (rulesMode === "guided" ? (
              <RuleBuilder project={p} onChange={change} />
            ) : (
              <JsonEditor
                key={rulesMode}
                label="Conditional trigger rules"
                defaultMode={rulesMode === "json" ? "json" : "form"}
                field={projectEditorFields(p).triggers}
                value={p.triggers}
                onSave={(v) => onChange(parseProject({ ...p, triggers: v }))}
              />
            ))}
          <DatasetManager project={p} onChange={onChange} notify={notify} />
        </section>
      )}
      {adding && (
        <AddFieldDialog
          project={p}
          onAdd={add}
          onClose={() => setAdding(false)}
        />
      )}
      {showForm && (
        <Modal title="Generated form" onClose={() => setShowForm(false)} wide>
          <HostFormData
            kind="profiles"
            project={p}
            onValues={setPreviewValues}
          />
          <FormGenerator
            maps={services.maps}
            dam={damServices}
            project={p}
            values={previewValues}
            upload={services.upload}
            assets={services.assets}
            assetUrl={services.assetUrl}
            loadAsset={services.loadAsset}
            transformImage={services.transformImage}
            removeBackground={services.removeBackground}
            partId={partId || undefined}
            onChange={setPreviewValues}
          />
        </Modal>
      )}
    </div>
  );
}
/** Form layout in three depths: drag and drop, every control, or JSON. */
function FormLayout({ project: p, onChange, notify }: EditorProps) {
  const [mode, setMode] = useState<SetupMode>("guided");
  return (
    <div className="form-layout">
      <div className="setup-mode-bar">
        <SetupModes
          label="Form layout setup"
          guidedLabel="Visual builder"
          value={mode}
          onChange={setMode}
        />
      </div>
      {mode === "guided" && (
        <FormLayoutBuilder project={p} onChange={onChange} />
      )}
      {mode === "advanced" && (
        <LayoutEditor project={p} onChange={onChange} notify={notify} />
      )}
      {mode === "json" && (
        <section className="panel padded">
          <h3>Form layout JSON</h3>
          <p className="muted">
            Sections, tabs and each field's place on the form. Field entries
            update existing fields only.
          </p>
          <JsonEditor
            label="Form layout JSON"
            defaultMode="json"
            value={{
              groups: p.groups,
              tabs: p.tabs,
              fields: [...p.fields]
                .sort((a, b) => a.order - b.order)
                .map(({ id, label, groupId, order, formDisplay }) => ({
                  id,
                  label,
                  groupId: groupId ?? null,
                  order,
                  formDisplay: formDisplay ?? "show",
                })),
            }}
            onSave={(value) => onChange(applyLayoutJson(p, value))}
          />
        </section>
      )}
    </div>
  );
}
function applyLayoutJson(p: Project, value: unknown): Project {
  const layout = value as {
    groups?: Project["groups"];
    tabs?: Project["tabs"];
    fields?: {
      id: string;
      label?: string;
      groupId?: string | null;
      order?: number;
      formDisplay?: Field["formDisplay"];
    }[];
  };
  if (!layout || typeof layout !== "object" || Array.isArray(layout))
    throw new Error("Form layout JSON must be an object");
  const entries = new Map((layout.fields || []).map((f) => [f.id, f]));
  const unknown = [...entries.keys()].filter(
    (id) => !p.fields.some((f) => f.id === id),
  );
  if (unknown.length)
    throw new Error(`Unknown field ids: ${unknown.join(", ")}`);
  return parseProject({
    ...p,
    groups: layout.groups ?? p.groups,
    tabs: layout.tabs ?? p.tabs,
    fields: p.fields.map((f) => {
      const entry = entries.get(f.id);
      if (!entry) return f;
      return {
        ...f,
        ...(entry.label !== undefined ? { label: entry.label } : {}),
        ...(entry.order !== undefined ? { order: entry.order } : {}),
        ...(entry.formDisplay !== undefined
          ? { formDisplay: entry.formDisplay }
          : {}),
        ...(entry.groupId !== undefined
          ? { groupId: entry.groupId || undefined }
          : {}),
      };
    }),
  });
}
function LayoutEditor({ project: p, onChange }: EditorProps) {
  const updateField = (id: string, patch: Partial<Field>) =>
    onChange({
      ...p,
      fields: p.fields.map((field) =>
        field.id === id ? { ...field, ...patch } : field,
      ),
    });
  const row = (field: Field) => (
    <div className="membership-row" key={field.id}>
      <span>
        {field.label}
        {(!field.editable || field.system || field.calculation) && (
          <small> · Read-only</small>
        )}
        {field.formDisplay === "hide" && <small> · Hidden on form</small>}
      </span>
      <div className="button-row">
        <Button
          aria-label={`${field.formDisplay === "hide" ? "Show" : "Hide"} ${field.label} on form`}
          onClick={() =>
            updateField(field.id, {
              formDisplay: field.formDisplay === "hide" ? "show" : "hide",
            })
          }
        >
          {field.formDisplay === "hide" ? "Show" : "Hide"}
        </Button>
        <Button
          aria-label={`Remove ${field.label} from form`}
          onClick={() => updateField(field.id, { formDisplay: "remove" })}
        >
          Remove
        </Button>
        {field.groupId && (
          <Button
            aria-label={`Unassign ${field.label} from section`}
            onClick={() => updateField(field.id, { groupId: undefined })}
          >
            Unassign
          </Button>
        )}
      </div>
    </div>
  );
  const update = (id: string, patch: Partial<Project["groups"][number]>) =>
    onChange({
      ...p,
      groups: p.groups.map((g) => (g.id === id ? { ...g, ...patch } : g)),
    });
  return (
    <div className="layout-editor">
      <div className="layout-toolbar">
        <p className="muted">
          Arrange the main project's form into up to three columns and reusable
          sections. Hide keeps a control here; Remove moves it to Available
          fields. Values and document bindings are retained. Read-only variables
          and fixed options can be shown alongside editable inputs. When
          sections exist, only fields assigned to a section appear in the form.
        </p>
        <Button
          onClick={() =>
            onChange({
              ...p,
              groups: [
                ...p.groups,
                {
                  id: uid("group"),
                  name: "New section",
                  column: 1,
                  order: p.groups.length,
                  visible: true,
                  note: "",
                },
              ],
            })
          }
        >
          <Plus size={16} />
          Add section
        </Button>
      </div>
      <div className="layout-columns">
        {[1, 2, 3].map((col) => (
          <div key={col} className="layout-column">
            <span className="eyebrow">COLUMN {col}</span>
            {p.groups
              .filter((g) => g.column === col)
              .sort((a, b) => a.order - b.order)
              .map((g) => (
                <section className="panel padded" key={g.id}>
                  <input
                    aria-label="Group name"
                    className="title-input"
                    value={g.name}
                    onChange={(e) => update(g.id, { name: e.target.value })}
                  />
                  <div className="control-grid">
                    <Control label="Column">
                      <Select
                        value={g.column}
                        onChange={(e) =>
                          update(g.id, { column: Number(e.target.value) })
                        }
                      >
                        {[1, 2, 3].map((c) => (
                          <option key={c}>{c}</option>
                        ))}
                      </Select>
                    </Control>
                    <Control label="Order">
                      <input
                        type="number"
                        value={g.order}
                        onChange={(e) =>
                          update(g.id, { order: Number(e.target.value) })
                        }
                      />
                    </Control>
                  </div>
                  <Toggle
                    label="Visible"
                    value={g.visible}
                    onChange={(v) => update(g.id, { visible: v })}
                  />
                  <Control label="Section instructions">
                    <input
                      value={g.note}
                      onChange={(e) => update(g.id, { note: e.target.value })}
                    />
                  </Control>
                  {p.fields
                    .filter(
                      (f) => f.groupId === g.id && f.formDisplay !== "remove",
                    )
                    .sort((a, b) => a.order - b.order)
                    .map(row)}
                  <Select
                    aria-label={`Assign field to ${g.name}`}
                    value=""
                    onChange={(e) =>
                      onChange({
                        ...p,
                        fields: p.fields.map((f) =>
                          f.id === e.target.value
                            ? { ...f, groupId: g.id, formDisplay: "show" }
                            : f,
                        ),
                      })
                    }
                  >
                    <option value="">+ Assign field</option>
                    {p.fields
                      .filter((f) => f.groupId !== g.id)
                      .map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.label}
                        </option>
                      ))}
                  </Select>
                  <button
                    className="text-button danger"
                    onClick={() =>
                      onChange({
                        ...p,
                        groups: p.groups.filter((x) => x.id !== g.id),
                        fields: p.fields.map((f) =>
                          f.groupId === g.id ? { ...f, groupId: undefined } : f,
                        ),
                        tabs: p.tabs.map((t) => ({
                          ...t,
                          groupIds: t.groupIds.filter((id) => id !== g.id),
                        })),
                        triggers: p.triggers.map((t) => ({
                          ...t,
                          show: t.show.filter((x) => x !== g.id),
                          hide: t.hide.filter((x) => x !== g.id),
                          clear: t.clear.filter((x) => x !== g.id),
                        })),
                      })
                    }
                  >
                    Delete section
                  </button>
                </section>
              ))}
          </div>
        ))}
      </div>
      <section className="panel padded" aria-label="Ungrouped form fields">
        <h3>Ungrouped fields</h3>
        <p className="muted">
          Assign these fields to a section to include them in a configured form.
          Without sections, the form shows all available fields.
        </p>
        {p.fields
          .filter((f) => !f.groupId && f.formDisplay !== "remove")
          .sort((a, b) => a.order - b.order)
          .map(row)}
      </section>
      <section className="panel padded" aria-label="Available form fields">
        <h3>Available fields</h3>
        <p>
          Removed controls can be added back with their settings and section
          intact. Part-specific form settings are available under Fields →
          Editing scope. A variable's Hidden setting and conditional rules also
          apply to its visibility.
        </p>
        {p.fields
          .filter((f) => f.formDisplay === "remove")
          .map((field) => (
            <div className="membership-row" key={field.id}>
              <span>{field.label}</span>
              <Button
                aria-label={`Add ${field.label} to form`}
                onClick={() => updateField(field.id, { formDisplay: "show" })}
              >
                Add to form
              </Button>
            </div>
          ))}
      </section>
      <section className="panel padded">
        <h3>Form tabs</h3>
        <JsonEditor
          label="Form tabs"
          field={projectEditorFields(p).tabs}
          value={p.tabs}
          onSave={(v) => onChange(parseProject({ ...p, tabs: v }))}
        />
      </section>
    </div>
  );
}
function PartEditor({
  project: p,
  onChange,
  onEdit,
}: { onEdit: (id: string) => void } & EditorProps) {
  const [newName, setNewName] = useState("");
  return (
    <section className="panel padded">
      <HostFormData
        kind="parts"
        onPart={(part) =>
          onChange(
            parseProject({
              ...p,
              parts: [...p.parts.filter((row) => row.id !== part.id), part],
            }),
          )
        }
      />
      <div className="section-heading">
        <div>
          <h3>Specialize a part</h3>
          <p className="muted">
            Parts keep only their explicit overrides. Unconfigured variables
            follow the main project.
          </p>
        </div>
        <div className="inline-form">
          <input
            aria-label="New part name"
            placeholder="Part name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <Button
            kind="primary"
            disabled={!newName.trim()}
            onClick={() => {
              const part: Part = {
                id: uid("part"),
                name: newName,
                sku: newName.toUpperCase().replace(/\s+/g, "-"),
                instructions: "",
                overrides: {},
                systemValues: {},
              };
              onChange({ ...p, parts: [...p.parts, part] });
              setNewName("");
            }}
          >
            <Plus size={14} />
            Add part
          </Button>
        </div>
      </div>
      {p.parts.map((part) => (
        <article className="part-card" key={part.id}>
          <div>
            <span className="badge">{part.sku}</span>
            <h3>{part.name}</h3>
            <p>
              {Object.keys(part.overrides).length} overridden variables ·{" "}
              {p.fields.length - Object.keys(part.overrides).length} inherited
            </p>
          </div>
          <Button onClick={() => onEdit(part.id)}>Configure overrides</Button>
          {(p.dynamicSources || []).map((source) => (
            <Control
              key={source.id}
              label={`Source requirement · ${source.name}`}
            >
              <Select
                value={
                  part.dynamicSourcePolicies &&
                  Object.hasOwn(part.dynamicSourcePolicies, source.id)
                    ? part.dynamicSourcePolicies[source.id]
                    : ""
                }
                onChange={(event) => {
                  const policies = { ...part.dynamicSourcePolicies };
                  if (event.target.value)
                    Object.defineProperty(policies, source.id, {
                      value: event.target.value as "required" | "optional",
                      enumerable: true,
                      writable: true,
                      configurable: true,
                    });
                  else delete policies[source.id];
                  onChange({
                    ...p,
                    parts: p.parts.map((item) =>
                      item.id === part.id
                        ? {
                            ...item,
                            dynamicSourcePolicies: Object.keys(policies).length
                              ? policies
                              : undefined,
                          }
                        : item,
                    ),
                  });
                }}
              >
                <option value="">Inherit project policy</option>
                <option value="required">Require source</option>
                <option value="optional">Allow skip</option>
              </Select>
            </Control>
          ))}
          <Control
            label="Part instructions"
            note="Leave blank to inherit project instructions."
          >
            <textarea
              rows={2}
              value={part.instructions}
              onChange={(e) =>
                onChange({
                  ...p,
                  parts: p.parts.map((x) =>
                    x.id === part.id
                      ? { ...x, instructions: e.target.value }
                      : x,
                  ),
                })
              }
            />
          </Control>
          <details>
            <summary>Explicit system values</summary>
            <JsonEditor
              value={part.systemValues}
              onSave={(v) =>
                onChange({
                  ...p,
                  parts: p.parts.map((x) =>
                    x.id === part.id ? { ...x, systemValues: v as Values } : x,
                  ),
                })
              }
            />
          </details>
          <details>
            <summary>Persisted overrides</summary>
            <JsonEditor
              value={part.overrides}
              onSave={(v) =>
                onChange({
                  ...p,
                  parts: p.parts.map((x) =>
                    x.id === part.id
                      ? { ...x, overrides: v as Part["overrides"] }
                      : x,
                  ),
                })
              }
            />
          </details>
          <button
            className="text-button danger"
            onClick={() =>
              onChange({ ...p, parts: p.parts.filter((x) => x.id !== part.id) })
            }
          >
            Remove part
          </button>
        </article>
      ))}
    </section>
  );
}
import type { Values } from "@proof/core";
/**
 * Reusable forms as their own module in the management application. The
 * primary field editor no longer carries reusable-form management.
 */
/** Detailed Control type help: when to use each kind of control. */
function ControlTypeHelp() {
  const groups = [...new Set(fieldTypes.map((t) => fieldTypeInfo[t].group))];
  return (
    <>
      <p>
        The control type decides how people enter the value and which validation
        and formatting settings apply. Changing it keeps only the settings that
        still fit.
      </p>
      {groups.map((group) => (
        <section key={group}>
          <h3>{group}</h3>
          <ul>
            {fieldTypes
              .filter((t) => fieldTypeInfo[t].group === group)
              .map((t) => (
                <li key={t}>
                  <strong>{fieldTypeInfo[t].name}</strong> —{" "}
                  {fieldTypeInfo[t].description}
                </li>
              ))}
          </ul>
        </section>
      ))}
    </>
  );
}
export function ReusableFormsModule({
  project,
  onChange,
  notify,
}: EditorProps) {
  const change = (next: Project) => {
    try {
      onChange(parseProject(next));
    } catch (e) {
      notify((e as Error).message);
    }
  };
  return (
    <div className="configuration reusable-forms-module">
      <div className="page-intro">
        <div>
          <h1>Reusable forms</h1>
          <p>
            Attach shared forms to this project and map each instance to its
            variables.
          </p>
        </div>
      </div>
      <ReusableForms project={project} onChange={change} notify={notify} />
    </div>
  );
}
function ReusableForms({ project: p, onChange }: EditorProps) {
  return (
    <section className="panel padded">
      <div className="section-heading">
        <div>
          <h3>Reusable form definitions</h3>
          <p className="muted">
            Attach a form more than once. Each instance has an independent field
            mapping.
          </p>
        </div>
        <Button
          onClick={() =>
            onChange({
              ...p,
              forms: [
                ...p.forms,
                {
                  id: uid("form"),
                  name: "Contact details",
                  fields: [newField("text")],
                  allowCreate: true,
                },
              ],
            })
          }
        >
          <Plus size={16} />
          Add reusable form
        </Button>
      </div>
      <JsonEditor
        label="Reusable forms"
        field={projectEditorFields(p).forms}
        value={p.forms}
        onSave={(v) => onChange(parseProject({ ...p, forms: v }))}
      />
      <h3>Form instances</h3>
      {p.formInstances.some((i) => i.profileOnly) && (
        <p>
          Manage global profile collection assignments under Configuration →
          Global Profiles.
        </p>
      )}
      {p.formInstances
        .filter((i) => !i.profileOnly)
        .map((i) => (
          <article className="part-card" key={i.id}>
            <Control label="Instance label">
              <input
                value={i.label}
                onChange={(e) =>
                  onChange({
                    ...p,
                    formInstances: p.formInstances.map((x) =>
                      x.id === i.id ? { ...x, label: e.target.value } : x,
                    ),
                  })
                }
              />
            </Control>
            <Button
              onClick={() => {
                const form = p.forms.find((f) => f.id === i.formId)!;
                const suggested = suggestFormMapping(
                  form.fields.filter((f) => !i.mapping[f.id]),
                  p.fields.filter(
                    (f) => !Object.values(i.mapping).includes(f.id),
                  ),
                );
                onChange({
                  ...p,
                  formInstances: p.formInstances.map((x) =>
                    x.id === i.id
                      ? {
                          ...x,
                          mapping: { ...x.mapping, ...suggested.mapping },
                        }
                      : x,
                  ),
                });
              }}
            >
              Suggest variable mappings
            </Button>
            <Toggle
              label="Show only mapped form inputs"
              value={!!i.mappedOnly}
              onChange={(mappedOnly) =>
                onChange({
                  ...p,
                  formInstances: p.formInstances.map((x) =>
                    x.id === i.id ? { ...x, mappedOnly } : x,
                  ),
                })
              }
            />
            {p.forms
              .find((f) => f.id === i.formId)
              ?.fields.map((f) => (
                <div key={f.id}>
                  <Control label={`Map ${f.label} to variable`}>
                    <Select
                      value={i.mapping[f.id] || ""}
                      onChange={(e) => {
                        const mapping = { ...i.mapping };
                        if (e.target.value) mapping[f.id] = e.target.value;
                        else delete mapping[f.id];
                        onChange({
                          ...p,
                          formInstances: p.formInstances.map((x) =>
                            x.id === i.id ? { ...x, mapping } : x,
                          ),
                        });
                      }}
                    >
                      <option value="">Unmapped</option>
                      {p.fields
                        .filter((field) => formFieldCompatible(f, field))
                        .map((field) => (
                          <option key={field.id} value={field.id}>
                            {field.label}
                          </option>
                        ))}
                    </Select>
                  </Control>
                  {!i.mapping[f.id] ? (
                    <Button
                      onClick={() => onChange(exposeFormInput(p, i.id, f.id))}
                    >
                      Expose {f.label} without artwork
                    </Button>
                  ) : (
                    <small>
                      {i.inputOnly?.includes(f.id)
                        ? "Exposed input; no artwork binding required."
                        : "Mapped to a project variable."}
                    </small>
                  )}
                </div>
              ))}
            <button
              className="text-button danger"
              onClick={() =>
                onChange({
                  ...p,
                  formInstances: p.formInstances.filter((x) => x.id !== i.id),
                })
              }
            >
              Remove instance
            </button>
          </article>
        ))}
      <Select
        aria-label="Attach reusable form"
        value=""
        onChange={(e) => {
          const form = p.forms.find((f) => f.id === e.target.value)!;
          onChange({
            ...p,
            formInstances: [
              ...p.formInstances,
              {
                id: uid("instance"),
                formId: form.id,
                label: `${form.name} ${p.formInstances.length + 1}`,
                mapping: suggestFormMapping(form.fields, p.fields).mapping,
                mappedOnly: true,
              },
            ],
          });
        }}
      >
        <option value="">+ Attach a form instance</option>
        {p.forms.map((f) => (
          <option key={f.id} value={f.id}>
            {f.name}
          </option>
        ))}
      </Select>
    </section>
  );
}

/**
 * QR code setup: pick a standard type, then decide where each part comes from
 * — asked on the form (default), another field, or a fixed answer.
 */
function MapSources({
  field,
  fields,
  onChange,
}: {
  field: Field;
  fields: Field[];
  onChange: (map: NonNullable<Field["map"]>) => void;
}) {
  const map = mapSettingsSchema.parse(field.map || {});
  const parts =
    map.locationMode === "coordinates"
      ? coordinateParts
      : map.locationMode === "image"
        ? []
        : addressParts;
  const source = (key: string) =>
    map.disabledParts?.includes(key)
      ? "disabled"
      : map.linked?.[key]
        ? `linked:${map.linked[key]}`
        : map.fixed?.[key] !== undefined
          ? "fixed"
          : map.defaults?.[key] || map.mapping?.[key]
            ? `default:${map.defaults?.[key] || map.mapping?.[key]}`
            : "ask";
  return (
    <>
      <Control label="Default map source">
        <Select
          value={map.locationMode || "address"}
          onChange={(e) =>
            onChange({
              ...map,
              locationMode: e.target.value as NonNullable<
                Field["map"]
              >["locationMode"],
            })
          }
        >
          <option value="address">Address</option>
          <option
            value="coordinates"
            disabled={["latitude", "longitude"].some((key) =>
              map.disabledParts?.includes(key),
            )}
          >
            Latitude / longitude
          </option>
          <option value="image">Upload an image</option>
        </Select>
      </Control>
      <Toggle
        label="Allow latitude / longitude inputs"
        value={
          !["latitude", "longitude"].some((key) =>
            map.disabledParts?.includes(key),
          )
        }
        onChange={(enabled) =>
          onChange({
            ...map,
            disabledParts: [
              ...(map.disabledParts || []).filter(
                (key) => !["latitude", "longitude"].includes(key),
              ),
              ...(!enabled ? ["latitude", "longitude"] : []),
            ],
            ...(!enabled && map.locationMode === "coordinates"
              ? { locationMode: "address" as const }
              : {}),
          })
        }
      />
      <p className="muted">
        Choose the initial source. People can switch sources or upload map
        artwork on the form. Configure each part as an editable default, a
        linked value, or a fixed answer. Generating a map remains an explicit
        action.
      </p>
      {parts.map((part) => (
        <div key={part.key}>
          <Control label={`${part.label} source`}>
            <Select
              aria-label={`${part.label} map source`}
              value={source(part.key)}
              onChange={(e) => {
                const linked = { ...map.linked },
                  defaults = { ...map.defaults },
                  fixed = { ...map.fixed },
                  mapping = { ...map.mapping };
                for (const record of [linked, defaults, fixed, mapping])
                  delete record[part.key];
                const next = e.target.value;
                if (next.startsWith("linked:"))
                  linked[part.key] = next.slice(7);
                if (next.startsWith("default:"))
                  defaults[part.key] = next.slice(8);
                if (next === "fixed") fixed[part.key] = "";
                const disabledParts = (map.disabledParts || []).filter(
                  (key) => key !== part.key,
                );
                if (next === "disabled") disabledParts.push(part.key);
                onChange({
                  ...map,
                  linked,
                  defaults,
                  fixed,
                  mapping,
                  disabledParts,
                });
              }}
            >
              <option value="disabled">Not used (hide from form)</option>
              <option value="ask">Ask on the form</option>
              <option value="fixed">Fixed answer</option>
              <optgroup label="Editable default from another field">
                {fields
                  .filter((f) => f.id !== field.id)
                  .map((f) => (
                    <option key={f.id} value={`default:${f.id}`}>
                      {f.label} ({f.key})
                    </option>
                  ))}
              </optgroup>
              <optgroup label="Linked to another field">
                {fields
                  .filter((f) => f.id !== field.id)
                  .map((f) => (
                    <option key={f.id} value={`linked:${f.id}`}>
                      {f.label} ({f.key})
                    </option>
                  ))}
              </optgroup>
            </Select>
          </Control>
          {source(part.key) === "fixed" && (
            <Control label={`${part.label} answer`}>
              <input
                value={map.fixed?.[part.key] || ""}
                onChange={(e) =>
                  onChange({
                    ...map,
                    fixed: { ...map.fixed, [part.key]: e.target.value },
                  })
                }
              />
            </Control>
          )}
        </div>
      ))}
    </>
  );
}

function QrSettings({
  field,
  fields,
  onChange,
}: {
  field: Field;
  fields: Field[];
  onChange: (qr: NonNullable<Field["qr"]>) => void;
}) {
  const qr: NonNullable<Field["qr"]> = {
    kind: field.qr?.kind || "website",
    mapping: field.qr?.mapping || {},
    foreground: field.qr?.foreground || "#183c3c",
    background: field.qr?.background || "transparent",
    ...(field.qr?.disabledParts
      ? { disabledParts: field.qr.disabledParts }
      : {}),
    ...(field.qr?.locationMode ? { locationMode: field.qr.locationMode } : {}),
    ...(field.qr?.defaults ? { defaults: field.qr.defaults } : {}),
    ...(field.qr?.fixed ? { fixed: field.qr.fixed } : {}),
  };
  const parts = qrParts({ ...field, qr: { ...qr, disabledParts: undefined } });
  const source = (key: string) =>
    qr.disabledParts?.includes(key)
      ? "disabled"
      : qr.mapping[key]
        ? `field:${qr.mapping[key]}`
        : qr.defaults?.[key]
          ? `default:${qr.defaults[key]}`
          : qr.fixed?.[key] !== undefined
            ? "fixed"
            : "ask";
  const setSource = (key: string, next: string) => {
    const mapping = { ...qr.mapping },
      defaults = { ...qr.defaults },
      fixed = { ...(qr.fixed || {}) };
    delete mapping[key];
    delete defaults[key];
    delete fixed[key];
    if (next.startsWith("field:")) mapping[key] = next.slice(6);
    if (next.startsWith("default:")) defaults[key] = next.slice(8);
    if (next === "fixed") fixed[key] = "";
    const disabledParts = (qr.disabledParts || []).filter(
      (part) => part !== key,
    );
    if (next === "disabled") disabledParts.push(key);
    onChange({ ...qr, mapping, defaults, fixed, disabledParts });
  };
  return (
    <div className="qr-settings">
      <Control label="QR code type">
        <Select
          value={qr.kind}
          onChange={(e) =>
            onChange({
              ...qr,
              kind: e.target.value as typeof qr.kind,
              mapping: {},
              defaults: {},
              disabledParts: [],
              fixed: {},
            })
          }
        >
          {Object.entries(qrTypes).map(([kind, info]) => (
            <option key={kind} value={kind}>
              {info.label}
            </option>
          ))}
        </Select>
      </Control>
      <p className="muted">
        Each part is asked on the form unless you fill it from another field or
        fix the answer. Defaults follow the form until edited in the QR editor;
        linked values stay tied to the form. Turn on Editable so people can fill
        the asked parts.
      </p>
      {qr.kind === "location" && (
        <>
          <Toggle
            label="Allow latitude / longitude inputs"
            value={
              !["latitude", "longitude"].some((key) =>
                qr.disabledParts?.includes(key),
              )
            }
            onChange={(enabled) =>
              onChange({
                ...qr,
                disabledParts: [
                  ...(qr.disabledParts || []).filter(
                    (key) => !["latitude", "longitude"].includes(key),
                  ),
                  ...(!enabled ? ["latitude", "longitude"] : []),
                ],
                ...(!enabled && qr.locationMode === "coordinates"
                  ? { locationMode: "address" as const }
                  : {}),
              })
            }
          />
          <Control label="QR location source">
            <Select
              value={qr.locationMode || "auto"}
              onChange={(e) =>
                onChange({
                  ...qr,
                  locationMode: e.target.value as NonNullable<
                    Field["qr"]
                  >["locationMode"],
                })
              }
            >
              <option value="address">Address</option>
              <option
                value="coordinates"
                disabled={["latitude", "longitude"].some((key) =>
                  qr.disabledParts?.includes(key),
                )}
              >
                Latitude / longitude
              </option>
              <option value="auto">
                Address or coordinates (use supplied coordinates first)
              </option>
            </Select>
          </Control>
        </>
      )}
      {parts.map((part) => (
        <div className="qr-settings-part" key={part.key}>
          <Control label={part.label}>
            <Select
              aria-label={`${part.label} source`}
              value={source(part.key)}
              onChange={(e) => setSource(part.key, e.target.value)}
            >
              <option value="disabled">Not used (hide from form)</option>
              <option value="ask">Ask on the form</option>
              <option value="fixed">Fixed answer</option>
              <optgroup label="Editable default from another field">
                {fields
                  .filter((f) => f.id !== field.id)
                  .map((f) => (
                    <option key={f.id} value={`default:${f.id}`}>
                      {f.label} ({f.key})
                    </option>
                  ))}
              </optgroup>
              <optgroup label="Linked to another field">
                {fields
                  .filter((f) => f.id !== field.id)
                  .map((f) => (
                    <option key={f.id} value={`field:${f.id}`}>
                      {f.label}
                    </option>
                  ))}
              </optgroup>
            </Select>
          </Control>
          {source(part.key) === "fixed" && (
            <Control label={`${part.label} answer`}>
              {part.input === "select" ? (
                <Select
                  value={qr.fixed?.[part.key] || ""}
                  onChange={(e) =>
                    onChange({
                      ...qr,
                      fixed: { ...qr.fixed, [part.key]: e.target.value },
                    })
                  }
                >
                  {part.options?.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              ) : (
                <input
                  type={
                    part.input && !["textarea", "checkbox"].includes(part.input)
                      ? part.input
                      : "text"
                  }
                  placeholder={part.placeholder}
                  value={qr.fixed?.[part.key] || ""}
                  onChange={(e) =>
                    onChange({
                      ...qr,
                      fixed: { ...qr.fixed, [part.key]: e.target.value },
                    })
                  }
                />
              )}
            </Control>
          )}
        </div>
      ))}
      <details>
        <summary>Advanced: colors</summary>
        <div className="qr-colors">
          <Control label="Code color">
            <input
              type="color"
              value={
                /^#[0-9a-f]{6}$/i.test(qr.foreground)
                  ? qr.foreground
                  : "#183c3c"
              }
              onChange={(e) => onChange({ ...qr, foreground: e.target.value })}
            />
          </Control>
          <Control label="Background">
            <Select
              value={qr.background === "transparent" ? "transparent" : "color"}
              onChange={(e) =>
                onChange({
                  ...qr,
                  background:
                    e.target.value === "transparent"
                      ? "transparent"
                      : "#ffffff",
                })
              }
            >
              <option value="transparent">Transparent</option>
              <option value="color">Solid color</option>
            </Select>
          </Control>
          {qr.background !== "transparent" && (
            <Control label="Background color">
              <input
                type="color"
                value={
                  /^#[0-9a-f]{6}$/i.test(qr.background)
                    ? qr.background
                    : "#ffffff"
                }
                onChange={(e) =>
                  onChange({ ...qr, background: e.target.value })
                }
              />
            </Control>
          )}
        </div>
      </details>
    </div>
  );
}
/**
 * One-click global forms from the document's own fields: either a reusable
 * form linked back to this project, or a global profile type mapped to it.
 */
function GlobalFormDialog({
  project,
  kind,
  onClose,
  onDone,
}: {
  project: Project;
  kind: "form" | "profile";
  onClose(): void;
  onDone(project: Project, message: string): void;
}) {
  const services = useAuthoringServices();
  const [source, setSource] = useState(project),
    [linked, setLinked] = useState(false);
  const [name, setName] = useState(
      `${project.name} ${kind === "form" ? "form" : "profile"}`.slice(0, 200),
    ),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  let preview: ReturnType<typeof suggestFormFromDocument> | undefined,
    previewError = "";
  try {
    preview = suggestFormFromDocument(source);
  } catch (e) {
    previewError = (e as Error).message;
  }
  const run = async () => {
    if (!preview || !services.saveResource || busy) return;
    setBusy(true);
    setError("");
    try {
      const value = reusableFormSchema.parse(
        kind === "profile"
          ? {
              ...preview.value,
              purpose: "profile-collection",
              application: "mapped",
            }
          : { ...preview.value, application: "mapped" },
      );
      const saved = await services.saveResource({
        id: uid("resource"),
        tenantId: project.tenantId,
        kind: "form",
        name: name.trim(),
        version: 0,
        value: {
          ...value,
          extra_json_data: {
            version: 1,
            builder: formBuilderFromDocument(source),
            linkedProjectIds: linked ? [source.id] : [],
          },
        },
      });
      if (!linked || source.id !== project.id) {
        onDone(
          project,
          `Saved ${saved.name} to the shared library. ${linked ? `Future input-setting changes follow the ${source.name} draft.` : "Assign it to projects manually when ready."}`,
        );
        return;
      }
      if (kind === "profile")
        onDone(
          assignGlobalProfileCollection(project, saved),
          `Global profile type ${saved.name} created and mapped. Add profiles in Configuration → Global profiles.`,
        );
      else
        onDone(
          applyResourceSnapshot(project, saved, { formApplication: "mapped" }),
          `Saved ${saved.name} to the shared library and linked it to this project.`,
        );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      title={
        kind === "form"
          ? "Generate global form"
          : "Generate global profile form"
      }
      onClose={() => !busy && onClose()}
    >
      <DocumentFormSource
        project={project}
        label="Choose source project and copy/link behavior"
        onSelect={(selected, follow) => {
          setSource(selected);
          setLinked(follow);
          setName(
            `${selected.name} ${kind === "form" ? "form" : "profile"}`.slice(
              0,
              200,
            ),
          );
        }}
      />
      <Control label="Future changes">
        <Select
          value={linked ? "linked" : "copy"}
          onChange={(event) => setLinked(event.target.value === "linked")}
        >
          <option value="copy">Independent copy — assign manually</option>
          <option value="linked">
            Link source project — follow later changes
          </option>
        </Select>
      </Control>
      <p>
        Source: {source.name} · {linked ? "Linked draft" : "Independent copy"}
      </p>
      <p className="muted">
        {kind === "form"
          ? "Creates a shared form from the selected document. Choose whether to follow future changes or keep an independent copy."
          : "Creates a global profile type from this document's editable fields and maps it to the matching variables, so saved profiles can fill this form."}
      </p>
      {previewError ? (
        <p role="alert">{previewError}</p>
      ) : (
        <>
          <Control label={kind === "form" ? "Form name" : "Profile type name"}>
            <input
              value={name}
              maxLength={200}
              onChange={(event) => setName(event.target.value)}
            />
          </Control>
          <p>
            {preview!.value.fields.length} fields included
            {preview!.skipped.length
              ? ` · skipped ${preview!.skipped.join(", ")} (system, calculated or data-bound)`
              : ""}
            .
          </p>
        </>
      )}
      {error && <p role="alert">{error}</p>}
      <div className="button-row">
        <Button
          kind="primary"
          disabled={!preview || !name.trim() || busy}
          onClick={() => void run()}
        >
          {busy
            ? "Saving…"
            : kind === "form"
              ? linked
                ? "Save & link"
                : "Save independent copy"
              : linked
                ? "Generate & map"
                : "Generate independent profile"}
        </Button>
        <Button disabled={busy} onClick={onClose}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
}
